import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { TakingAPaymentBack } from '../../components/TakingAPaymentBack';
import { formatMoney } from '../../lib/format';
import { BillSubject, type BillSubjectFields } from './billSubject';

/**
 * WHAT HAS ACTUALLY BEEN PAID, and taking one back.
 *
 * A bill carried a paid figure and nothing could say what made it up. So L 10,000.00 typed where
 * L 1,000.00 was meant settled the bill for ever, took ten thousand off the register's expectation for
 * ever, and left nothing on any screen to point at when the drawer came up out against whoever was
 * standing at it. The delivery had a correction door and the bill had a credit note; the payment
 * had neither, and it did not even have a list.
 *
 * Reversed rows are shown, not hidden, and so are the payments they undo. "L 10,000.00 went out on
 * Tuesday and came back on Friday" is exactly the story somebody is here to find, and a list that
 * quietly dropped either half would agree with a tidier version of events than the one that
 * happened — while the money really did move both ways.
 */

const PAYMENTS_PATH = '/admin/v1/suppliers/supplier-payments';
const PAYABLES_PATH = '/admin/v1/suppliers/payables';
const BILLS_PATH = '/admin/v1/suppliers/supplier-invoices';

/** One row of the API's reply, flattened so the table can address its columns. */
interface PaymentRow extends BillSubjectFields {
  id: string;
  supplierId: string;
  amountMinor: number;
  currency: string;
  method: string;
  paidAt: string;
  paidByName: string | null;
  reversalOfPaymentId: string | null;
  reversalReason: string | null;
  reversedByPaymentId: string | null;
}

const asRow = (r: Record<string, unknown>) => r as unknown as PaymentRow;

export function PaymentsSection() {
  const queryClient = useQueryClient();
  const [undoing, setUndoing] = useState<PaymentRow | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(PAYMENTS_PATH) });
    // The bill's paid figure and its status move with it, on both lists that show them.
    void queryClient.invalidateQueries({ queryKey: listKey(PAYABLES_PATH) });
    void queryClient.invalidateQueries({ queryKey: listKey(BILLS_PATH) });
  };

  return (
    <>
      <CrudSection
        title="Payments made"
        description="Every payment recorded against a supplier bill, newest first — including ones that have been taken back. What a bill has been paid is the sum of its rows."
        listPath={PAYMENTS_PATH}
        emptyMessage="No payments recorded yet."
        extractList={(d) =>
          (
            d as {
              payment: Record<string, unknown>;
              supplierId: string;
              bill: Record<string, unknown>;
              reversedByPaymentId: string | null;
              paidByName: string | null;
            }[]
          ).map((x) => ({
            ...x.payment,
            supplierId: x.supplierId,
            description: x.bill.description,
            invoiceNumber: x.bill.invoiceNumber,
            receiptId: x.bill.receiptId,
            reversedByPaymentId: x.reversedByPaymentId,
            paidByName: x.paidByName,
          }))
        }
        // A row that has been undone, and the row that undid it, are both greyed — the same
        // treatment a superseded drawer count already gets, so a reader who has seen one screen
        // knows what it means on this one.
        rowBackgroundColor={(row) =>
          asRow(row).reversedByPaymentId || asRow(row).reversalOfPaymentId
            ? 'var(--mantine-color-gray-light)'
            : undefined
        }
        columns={[
          { accessor: 'paidAt', title: 'When', unit: 'datetime', noWrap: true },
          { accessor: 'supplierId', title: 'Supplier', reference: 'supplier' },
          {
            accessor: 'invoiceNumber',
            title: 'What for',
            render: (r) => <BillSubject bill={asRow(r)} />,
          },
          {
            accessor: 'amountMinor',
            title: 'Amount',
            noWrap: true,
            render: (r) => {
              const row = asRow(r);
              const taken = row.amountMinor < 0;
              return (
                <Stack gap={0} align="flex-start">
                  <Text size="sm" fw={600} c={taken ? 'red.7' : undefined}>
                    {formatMoney(row.amountMinor, row.currency)}
                  </Text>
                  {taken && (
                    <Badge size="xs" color="red" variant="light">
                      Taken back
                    </Badge>
                  )}
                  {row.reversedByPaymentId && (
                    <Badge size="xs" color="gray" variant="light">
                      Undone
                    </Badge>
                  )}
                </Stack>
              );
            },
          },
          { accessor: 'method', title: 'How', enumGroup: 'supplierPaymentMethod' },
          {
            accessor: 'reference',
            title: 'Note',
            // The reason a payment was taken back belongs on the row that took it back, beside the
            // figure it moved. Anywhere else and somebody reading the money has to go looking.
            render: (r) => {
              const row = asRow(r);
              return row.reversalReason ? (
                <Text size="xs" c="dimmed">
                  {row.reversalReason}
                </Text>
              ) : (
                <Text size="xs" c="dimmed">
                  {String((r.reference as string | null) ?? '')}
                </Text>
              );
            },
          },
          { accessor: 'paidByName', title: 'By', render: (r) => asRow(r).paidByName ?? '—' },
        ]}
        rowActions={(row) => {
          const payment = asRow(row);
          // Nothing to undo on a row that IS an undo, and nothing to undo twice. Both are refused
          // by the API as well; the button is hidden so the refusal never has to be read.
          if (payment.reversalOfPaymentId || payment.reversedByPaymentId) return null;
          return (
            <Group gap="xs" wrap="nowrap">
              <Button size="xs" variant="light" color="yellow" onClick={() => setUndoing(payment)}>
                Take it back
              </Button>
            </Group>
          );
        }}
      />

      {undoing && (
        <TakingAPaymentBack
          path={`${PAYMENTS_PATH}/${undoing.id}/reverse`}
          subject="this supplier payment"
          amountMinor={undoing.amountMinor}
          currency={undoing.currency}
          consequence="The bill goes back to owing it."
          onClose={() => setUndoing(null)}
          onDone={refresh}
        />
      )}
    </>
  );
}
